export type ProfitPeriodMode = 'today' | 'week' | 'month' | 'year' | 'custom';

export interface DateRange {
  /** Inicio inclusivo (hora local). */
  start: Date;
  /** Fin exclusivo (hora local). */
  end: Date;
}

export const PROFIT_PERIOD_OPTIONS: { value: ProfitPeriodMode; label: string }[] = [
  { value: 'today', label: 'Hoy' },
  { value: 'week', label: 'Esta semana' },
  { value: 'month', label: 'Este mes' },
  { value: 'year', label: 'Este año' },
  { value: 'custom', label: 'Personalizado' },
];

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d: Date, days: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + days);
  return x;
}

/** Rango [inicio, fin) para un modo de período; soporta rango personalizado. */
export function profitRange(
  mode: ProfitPeriodMode,
  customStart?: string,
  customEnd?: string
): DateRange {
  const now = new Date();
  const today = startOfDay(now);

  switch (mode) {
    case 'week': {
      // Semana que inicia el lunes.
      const day = today.getDay(); // 0 domingo
      const diff = day === 0 ? 6 : day - 1;
      const start = addDays(today, -diff);
      return { start, end: addDays(start, 7) };
    }
    case 'month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      return { start, end };
    }
    case 'year': {
      const start = new Date(today.getFullYear(), 0, 1);
      const end = new Date(today.getFullYear() + 1, 0, 1);
      return { start, end };
    }
    case 'custom': {
      const start = customStart ? startOfDay(new Date(`${customStart}T00:00:00`)) : today;
      const endBase = customEnd ? startOfDay(new Date(`${customEnd}T00:00:00`)) : start;
      const end = addDays(endBase, 1);
      return { start, end: end > start ? end : addDays(start, 1) };
    }
    case 'today':
    default:
      return { start: today, end: addDays(today, 1) };
  }
}

/** Rango inmediatamente anterior de la misma duración (para comparaciones). */
export function previousRange(range: DateRange): DateRange {
  const length = range.end.getTime() - range.start.getTime();
  return { start: new Date(range.start.getTime() - length), end: new Date(range.start.getTime()) };
}

export interface ProductProfitRow {
  productId: string | null;
  name: string;
  sku: string | null;
  categoryId: string | null;
  categoryName: string | null;
  purchasePrice: number;
  salePrice: number;
  quantity: number;
  revenue: number;
  cost: number;
  profit: number;
  margin: number;
}

export interface ProfitSummary {
  salesCount: number;
  quantity: number;
  revenue: number;
  cost: number;
  profit: number;
  margin: number;
  tax: number;
  refunds: number;
}

export const EMPTY_PROFIT_SUMMARY: ProfitSummary = {
  salesCount: 0,
  quantity: 0,
  revenue: 0,
  cost: 0,
  profit: 0,
  margin: 0,
  tax: 0,
  refunds: 0,
};

export type ProfitSortKey = 'profit' | 'quantity' | 'revenue' | 'margin';

export const PROFIT_SORT_OPTIONS: { value: ProfitSortKey; label: string }[] = [
  { value: 'profit', label: 'Mayor ganancia' },
  { value: 'quantity', label: 'Mayor cantidad vendida' },
  { value: 'revenue', label: 'Mayor ingreso' },
  { value: 'margin', label: 'Mayor margen' },
];

/** Variación porcentual respecto al período anterior, o null si no hay base. */
export function profitChange(current: number, previous: number): number | null {
  if (!Number.isFinite(previous) || Math.abs(previous) < 0.005) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
}

function round2(n: number): number {
  return Math.round((Number.isFinite(n) ? n : 0) * 100) / 100;
}

export interface ProfitProductMeta {
  id: string;
  sku: string | null;
  category_id: string | null;
  purchase_price: number | null;
  sale_price: number;
}

interface ProfitCategoryMeta {
  id: string;
  name: string;
}

/**
 * Calcula ganancias reales por producto a partir de las ventas y sus renglones.
 * - Excluye unidades devueltas (reembolsos parciales).
 * - Reparte el descuento por código proporcionalmente al renglón.
 * - Usa el costo congelado en la venta (unit_cost) y cae al costo actual si falta.
 */
export function computeProfits(
  sales: { id: string; subtotal: number; total: number; discount_code_amount: number | null; tax: number; refund_total: number | null }[],
  items: {
    sale_id: string;
    product_id: string | null;
    product_name: string;
    quantity: number;
    subtotal: number;
    unit_cost: number | null;
    returned_quantity: number | null;
  }[],
  products: ProfitProductMeta[],
  categories: ProfitCategoryMeta[]
): { rows: ProductProfitRow[]; summary: ProfitSummary } {
  const productMap = new Map(products.map((p) => [p.id, p]));
  const categoryMap = new Map(categories.map((c) => [c.id, c.name]));
  const saleMap = new Map(sales.map((s) => [s.id, s]));

  const rowMap = new Map<string, ProductProfitRow>();
  let revenue = 0;
  let cost = 0;
  let quantity = 0;
  let tax = 0;
  let refunds = 0;

  for (const item of items) {
    const sale = saleMap.get(item.sale_id);
    if (!sale) continue;

    const qty = Number(item.quantity || 0);
    if (qty <= 0) continue;
    const returnedQty = Math.min(Number(item.returned_quantity || 0), qty);
    const soldQty = qty - returnedQty;

    const unitRevenue = Number(item.subtotal || 0) / qty;
    const lineRevenue = unitRevenue * soldQty;
    refunds += unitRevenue * returnedQty;

    const codeDiscount = Number(sale.discount_code_amount || 0);
    const codeShare =
      codeDiscount > 0 && Number(sale.subtotal || 0) > 0
        ? codeDiscount * (Number(item.subtotal || 0) / Number(sale.subtotal || 0)) * (soldQty / qty)
        : 0;
    const netRevenue = lineRevenue - codeShare;

    const meta = item.product_id ? productMap.get(item.product_id) : undefined;
    const unitCost = Number(item.unit_cost ?? meta?.purchase_price ?? 0);
    const lineCost = unitCost * soldQty;

    revenue += netRevenue;
    cost += lineCost;
    quantity += soldQty;
    tax +=
      codeDiscount > 0 || returnedQty > 0
        ? 0
        : Number(sale.tax || 0) * (Number(item.subtotal || 0) / Math.max(Number(sale.subtotal || 1), 1));

    const key = item.product_id ?? `name:${item.product_name}`;
    const entry =
      rowMap.get(key) ??
      {
        productId: item.product_id,
        name: item.product_name,
        sku: meta?.sku ?? null,
        categoryId: meta?.category_id ?? null,
        categoryName: meta?.category_id ? categoryMap.get(meta.category_id) ?? null : null,
        purchasePrice: unitCost,
        salePrice: unitRevenue,
        quantity: 0,
        revenue: 0,
        cost: 0,
        profit: 0,
        margin: 0,
      };

    entry.quantity += soldQty;
    entry.revenue = round2(entry.revenue + netRevenue);
    entry.cost = round2(entry.cost + lineCost);
    entry.profit = round2(entry.revenue - entry.cost);
    entry.margin = entry.revenue > 0 ? round2((entry.profit / entry.revenue) * 100) : 0;
    entry.salePrice = entry.quantity > 0 ? round2(entry.revenue / entry.quantity) : entry.salePrice;

    rowMap.set(key, entry);
  }

  const rows = [...rowMap.values()].filter((r) => r.quantity > 0);
  const summary: ProfitSummary = {
    salesCount: sales.length,
    quantity,
    revenue: round2(revenue),
    cost: round2(cost),
    profit: round2(revenue - cost),
    margin: revenue > 0 ? round2(((revenue - cost) / revenue) * 100) : 0,
    tax: round2(tax),
    refunds: round2(refunds),
  };

  return { rows, summary };
}